import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import ProgressBar from "@/components/common/ProgressBar";
import { ArrowLeft, ChatsCircle, Lightning, Note } from "@phosphor-icons/react";

export default function ParticipantDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/participants/${id}`); setD(data); } catch { /* noop */ }
      try { const { data } = await api.get(`/campaign-settings`); setSettings(data); } catch { /* noop */ }
      setLoading(false);
    })();
  }, [id]);

  if (loading) return <div className="p-8 text-gray-400">Loading…</div>;
  if (!d) return <div className="p-8 text-red-400">Not found</div>;
  const p = d.participant;
  const targetLot = settings?.milestones?.length
    ? Math.max(...settings.milestones.map((m) => Number(m.lot) || 0))
    : 30;

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto">
      <Link to="/participants" className="inline-flex items-center gap-2 text-xs text-gray-400 hover:text-white mb-4">
        <ArrowLeft size={14} /> All participants
      </Link>
      <PageHeader
        title={p.full_name}
        subtitle={
          <span className="font-mono text-xs">
            {p.broker_account_number} · {p.broker_server} · {p.account_type}
          </span>
        }
        breadcrumbs="Participants / Detail"
        testId="participant-detail"
        actions={
          <div className="flex gap-2 flex-wrap">
            <StatusBadge status={p.status} />
            <StatusBadge status={p.risk_status || "healthy"} />
            {p.balance_over_threshold === true && (
              <span
                data-testid="funded-badge"
                title={`Broker balance ≥ $${settings?.balance_threshold_usd || 100}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-emerald-500/10 border border-emerald-500/40 text-emerald-300"
              >
                Funded ≥ ${settings?.balance_threshold_usd || 100}
              </span>
            )}
            {p.balance_over_threshold === false && (
              <span
                data-testid="unfunded-badge"
                title={`Broker balance below $${settings?.balance_threshold_usd || 100}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-white/5 border border-white/15 text-gray-400"
              >
                Below ${settings?.balance_threshold_usd || 100}
              </span>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile + Progress */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-[#111827] border border-white/10 rounded-lg p-6">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">Progress to {targetLot} lot</p>
            <div className="flex items-baseline justify-between mb-4">
              <p className="font-mono text-4xl font-bold text-white">{(p.total_valid_lot || 0).toFixed(2)}</p>
              <p className="text-xs text-gray-500 font-mono">
                Next milestone: <span className="text-orange-400">{d.next_milestone || "—"}</span>
              </p>
            </div>
            <ProgressBar value={p.total_valid_lot || 0} />
            <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-3 gap-3 text-xs">
              <div>
                <p className="uppercase tracking-widest text-gray-500 text-[10px]">Silver (campaign)</p>
                <p className="font-mono text-lg text-orange-300">{(p.total_valid_lot || 0).toFixed(2)}</p>
              </div>
              <div>
                <p className="uppercase tracking-widest text-gray-500 text-[10px]">All symbols</p>
                <p className="font-mono text-lg text-blue-300">{(p.total_lot_all_symbols || 0).toFixed(2)}</p>
              </div>
              <div>
                <p className="uppercase tracking-widest text-gray-500 text-[10px]">Symbols traded</p>
                <p className="font-mono text-lg text-white">{Object.keys(p.symbol_breakdown || {}).length}</p>
              </div>
            </div>
            {(p.master_client_id || (p.trading_accounts && p.trading_accounts.length > 0)) && (
              <div className="mt-4 pt-4 border-t border-white/5">
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-[10px] uppercase tracking-widest text-gray-500">Linked accounts (aggregated)</p>
                  {p.master_client_id && (
                    <span data-testid="master-client-id" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/30 text-[10px] font-mono text-blue-300">
                      clientID · {p.master_client_id}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(p.trading_accounts && p.trading_accounts.length > 0
                    ? p.trading_accounts
                    : [p.broker_account_number]
                  ).map((acc) => (
                    <span
                      key={acc}
                      data-testid={`linked-account-${acc}`}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono ${
                        acc === p.broker_account_number
                          ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"
                          : "bg-white/5 border border-white/10 text-gray-300"
                      }`}
                    >
                      MT#{acc}
                      {acc === p.broker_account_number && p.trading_accounts?.length > 1 && (
                        <span className="text-[9px] text-emerald-400">primary</span>
                      )}
                    </span>
                  ))}
                </div>
                {p.trading_accounts?.length > 1 && (
                  <p className="text-[10px] text-gray-500 mt-2">
                    Lots aggregated across all {p.trading_accounts.length} accounts. Milestone triggers when combined total ≥ {targetLot}.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Balance / Equity (from broker API sync) */}
          {(p.current_balance !== undefined && p.current_balance !== null) && (
            <div className="bg-[#111827] border border-white/10 rounded-lg p-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-widest text-gray-500">Live Account · from broker API</p>
                {p.last_balance_sync_at && <p className="text-[10px] font-mono text-gray-500">synced {p.last_balance_sync_at.slice(0, 16).replace("T", " ")}</p>}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Info label={`Balance ${p.current_currency || ""}`} value={p.current_balance != null ? `${Number(p.current_balance).toFixed(2)}` : "—"} accent="text-emerald-400" />
                <Info label="Equity" value={p.current_equity != null ? `${Number(p.current_equity).toFixed(2)}` : "—"} accent="text-blue-300" />
                <Info label="Free margin" value={p.current_free_margin != null ? `${Number(p.current_free_margin).toFixed(2)}` : "—"} />
                <Info label="Leverage" value={p.current_leverage != null ? `1:${p.current_leverage}` : "—"} />
              </div>
              {p.account_active === false && (
                <p className="mt-3 text-xs text-red-300 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30">⚠ Account marked inactive at broker</p>
              )}
            </div>
          )}

          {/* Symbol breakdown */}
          {p.symbol_breakdown && Object.keys(p.symbol_breakdown).length > 0 && (
            <div className="bg-[#111827] border border-white/10 rounded-lg p-6">
              <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">Symbol breakdown · trading behavior</p>
              <div className="space-y-2">
                {Object.entries(p.symbol_breakdown)
                  .sort((a, b) => (b[1].lot || 0) - (a[1].lot || 0))
                  .map(([sym, stats]) => {
                    const isCampaign = sym.toUpperCase().includes("XAG") || sym.toUpperCase().includes("SILVER");
                    const total = Object.values(p.symbol_breakdown).reduce((s, v) => s + (v.lot || 0), 0);
                    const pct = total > 0 ? (stats.lot / total) * 100 : 0;
                    return (
                      <div key={sym}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <div className="flex items-center gap-2">
                            <span className={`font-mono ${isCampaign ? "text-orange-300 font-bold" : "text-gray-300"}`}>{sym}</span>
                            {isCampaign && <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-orange-500/10 text-orange-300 border border-orange-500/30">CAMPAIGN</span>}
                          </div>
                          <div className="flex items-center gap-3 text-xs font-mono">
                            <span className="text-white">{stats.lot.toFixed(2)} lot</span>
                            <span className="text-gray-500">{stats.count} trades</span>
                            <span className={stats.profit >= 0 ? "text-emerald-400" : "text-red-400"}>{stats.profit >= 0 ? "+" : ""}{stats.profit.toFixed(2)}</span>
                          </div>
                        </div>
                        <div className="w-full bg-[#0B0F19] rounded-full h-1.5 overflow-hidden">
                          <div className={`h-full rounded-full ${isCampaign ? "bg-gradient-to-r from-orange-500 to-orange-400" : "bg-blue-500/60"}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          <div className="bg-[#111827] border border-white/10 rounded-lg p-6">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">Profile</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Info label="Email" value={p.email} />
              <Info label="Phone" value={p.phone} />
              <Info label="Telegram" value={p.telegram_username} />
              <Info label="Deposit" value={p.deposit_amount ? `$${p.deposit_amount.toFixed(2)}` : "—"} />
              <Info label="Registered (system)" value={(p.registration_date || "").slice(0, 10)} />
              <Info
                label="Signed up with broker"
                value={p.broker_sign_up_date || "—"}
                accent={p.broker_sign_up_date ? "text-blue-300" : ""}
              />
              <Info label="Approved" value={(p.approval_date || "—").slice(0, 10)} />
              <Info label="Eligible from" value={(p.eligibility_start_date || "—").slice(0, 10)} />
              <Info label="Country" value={p.country} />
              <Info label="Trading platform" value={p.trading_platform} />
              <Info label="Registration method" value={p.registration_method} />
            </div>
            {p.rejected_reason && <p className="mt-3 text-xs text-red-300">Reject reason: {p.rejected_reason}</p>}
            {p.disqualification_reason && <p className="mt-3 text-xs text-red-300">DQ reason: {p.disqualification_reason}</p>}
          </div>

          <div data-testid="tnc-acceptance-panel" className="bg-[#111827] border border-white/10 rounded-lg p-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-widest text-gray-500">Terms &amp; Conditions Acceptance</p>
              {p.terms_accepted ? (
                <span data-testid="tnc-status-accepted" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                  ✓ Accepted
                </span>
              ) : (
                <span data-testid="tnc-status-missing" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono bg-orange-500/10 border border-orange-500/30 text-orange-300">
                  ⚠ Not recorded
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Info
                label="Accepted at (UTC)"
                value={p.terms_accepted_at ? p.terms_accepted_at.slice(0, 19).replace("T", " ") : "—"}
              />
              <Info
                label="Method"
                value={
                  p.terms_acceptance_method === "public_registration" ? "Public form (ticked)" :
                  p.terms_acceptance_method === "admin_manual" ? "Admin manual entry" :
                  p.terms_acceptance_method === "broker_auto" ? "Auto via XM registration" :
                  p.terms_acceptance_method === "backfill_legacy" ? "Legacy backfill" :
                  p.terms_acceptance_method || "—"
                }
              />
              {p.terms_accepted_by && <Info label="Recorded by" value={p.terms_accepted_by} />}
            </div>
            <p className="mt-3 text-[11px] text-gray-500">
              Per campaign T&amp;C, opening an XM trading account under referral{" "}
              <span className="font-mono text-orange-300">MBE26</span> constitutes auto-acceptance without prior review.
            </p>
          </div>

          <div className="bg-[#111827] border border-white/10 rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <p className="text-xs uppercase tracking-widest text-gray-500">Recent Trades ({d.trades.length})</p>
            </div>
            <div className="overflow-x-auto max-h-80">
              <table className="w-full text-sm">
                <thead className="bg-[#1F2937]/50 text-left text-[10px] uppercase tracking-widest text-gray-500">
                  <tr>
                    <th className="px-4 py-2">Ticket</th>
                    <th className="px-4 py-2">Symbol</th>
                    <th className="px-4 py-2 text-right">Lot</th>
                    <th className="px-4 py-2">Close time</th>
                    <th className="px-4 py-2 text-right">Profit</th>
                    <th className="px-4 py-2">Valid</th>
                  </tr>
                </thead>
                <tbody>
                  {d.trades.length === 0 ? <tr><td colSpan={6} className="p-6 text-center text-gray-500">No trades imported yet</td></tr> :
                  d.trades.slice(0, 50).map((t) => (
                    <tr key={t.id} className="border-b border-white/5">
                      <td className="px-4 py-2 font-mono text-gray-300">{t.trade_ticket}</td>
                      <td className="px-4 py-2 text-gray-300">{t.symbol}</td>
                      <td className="px-4 py-2 text-right font-mono text-white">{t.lot_volume.toFixed(2)}</td>
                      <td className="px-4 py-2 font-mono text-xs text-gray-400">{(t.close_time || "").slice(0, 16).replace("T", " ")}</td>
                      <td className={`px-4 py-2 text-right font-mono ${t.profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>{t.profit.toFixed(2)}</td>
                      <td className="px-4 py-2">
                        {t.out_of_window ? (
                          <span data-testid={`trade-out-of-window-${t.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-orange-500/15 border border-orange-500/40 text-orange-300">
                            Out-of-window
                          </span>
                        ) : t.is_valid ? (
                          <span className="text-emerald-400 text-xs">✓</span>
                        ) : (
                          <span className="text-gray-500 text-xs">{t.invalid_reason || "—"}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-[#111827] border border-white/10 rounded-lg p-5">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-2">
              <Lightning size={12} /> Milestones
            </p>
            <div className="space-y-2">
              {(settings?.milestones?.map((m) => Number(m.lot)) || [10, 20, 30]).map((lot) => {
                const m = d.milestones.find((x) => x.milestone_lot === lot);
                return (
                  <div key={lot} className="flex items-center justify-between p-2 rounded-md bg-[#0B0F19] border border-white/5">
                    <span className="font-mono text-sm text-white">{lot} lot</span>
                    {m ? <StatusBadge status={m.status} /> : <StatusBadge status="locked" />}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-[#111827] border border-white/10 rounded-lg p-5">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-2">
              <ChatsCircle size={12} /> CRM Notes
            </p>
            {d.crm_notes.length === 0 ? <p className="text-xs text-gray-500">No notes yet</p> :
              <div className="space-y-3">
                {d.crm_notes.slice(0, 10).map((n) => (
                  <div key={n.id} className="border-l-2 border-blue-500/40 pl-3">
                    <p className="text-sm text-white">{n.note}</p>
                    <p className="text-[10px] text-gray-500 mt-1 font-mono">{(n.created_at || "").slice(0, 16).replace("T", " ")} · {n.author_name}</p>
                  </div>
                ))}
              </div>
            }
          </div>

          <div className="bg-[#111827] border border-white/10 rounded-lg p-5">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-3 flex items-center gap-2">
              <Note size={12} /> Audit Timeline
            </p>
            {d.audit_logs.length === 0 ? <p className="text-xs text-gray-500">No audit events</p> :
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {d.audit_logs.slice(0, 30).map((a) => (
                  <div key={a.id} className="text-xs">
                    <p className="text-gray-300 font-mono">{a.action.replace(/_/g, " ")}</p>
                    <p className="text-[10px] text-gray-500">{(a.timestamp || "").slice(0, 16).replace("T", " ")} · {a.user_name || "system"}</p>
                  </div>
                ))}
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, accent }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
      <p className={`text-sm font-medium mt-0.5 truncate ${accent || "text-white"}`}>{value || "—"}</p>
    </div>
  );
}
